import { Coins } from 'lucide-react';
import { Progress } from '@/shared/components/ui/progress';
import { useSubscription } from '@/features/Subscriptions/hooks/useSubscription';
import { useSubscriptionSSE } from '@/features/Subscriptions/hooks/useSubscriptionSSE';
import { useOrganization } from '@/shared/hooks/api/useOrganization';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { useIsSuperAdminRole } from '@/shared/hooks/use-system-role';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { cn } from '@/infrastructure/lib/utils';
import { useTranslation } from 'react-i18next';

export const CreditCounter = () => {
  const { subscription, isLoading } = useSubscription();
  const { data: organizationProfile } = useOrganization();
  const { selectedOrganization } = useSuperAdminOrg();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { t, i18n } = useTranslation();

  // Map i18n language to locale format
  const getLocale = () => {
    const lang = i18n.language;
    if (lang.startsWith('pt')) return 'pt-BR';
    if (lang.startsWith('en')) return 'en-US';
    return 'en-US'; // Default fallback
  };

  // Conectar ao SSE para receber atualizações em tempo real
  // Fallback para a organização home só para quem não é superadmin — em escopo de
  // plataforma não há créditos a contar, e cair na home mostraria o contador de outra
  // organização.
  const effectiveOrgId =
    selectedOrganization?.id ?? (isSuperAdmin ? undefined : organizationProfile?.organization_id);
  const { isConnected } = useSubscriptionSSE({
    organizationId: effectiveOrgId,
    enabled: !!effectiveOrgId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border">
        <Skeleton className="h-4 w-4" />
        <Skeleton className="h-4 w-24" />
      </div>
    );
  }

  if (!subscription) {
    return null;
  }

  const { consumption, subscribed_plan } = subscription;
  
  // Não exibir se não houver limite de créditos definido
  if (!subscribed_plan?.credits?.limit) {
    return null;
  }

  const creditUsagePercent = (consumption.total / subscribed_plan.credits.limit) * 100;
  const creditsRemaining = Math.round((subscribed_plan.credits.limit - consumption.total) * 100) / 100;
  const isExhausted = creditUsagePercent >= 100;
  
  const getProgressColor = () => {
    if (creditUsagePercent >= 100) return "[&>div]:bg-red-500";
    if (creditUsagePercent >= 70) return "[&>div]:bg-orange-500";
    return "";
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors">
            <Coins className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex flex-col gap-1 min-w-[100px]">
              {isExhausted ? (
                <p className="text-xs font-semibold text-red-500">{t('subscription.credits.exhausted')}</p>
              ) : (
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1">
                    <span className="font-semibold">{Math.round(consumption.total * 100) / 100}</span>
                    <span className="text-muted-foreground">{t('subscription.credits.label')}</span>
                  </div>
                  <span className="text-muted-foreground">/ {Math.round(subscribed_plan.credits.limit * 100) / 100}</span>
                </div>
              )}
              <Progress 
                value={creditUsagePercent} 
                className={cn("h-1", getProgressColor())} 
              />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">
            <div className="flex items-center justify-between mb-1">
              <p className="font-semibold">{t('subscription.credits.title')}</p>
              <div className={cn(
                "w-2 h-2 rounded-full",
                isConnected ? "bg-green-500 animate-pulse" : "bg-gray-400"
              )} title={isConnected ? "Atualizações em tempo real ativas" : "Sem atualizações em tempo real"} />
            </div>
            <p>{t('subscription.credits.used')}: {(Math.round(consumption.total * 100) / 100).toLocaleString(getLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
            <p>{t('subscription.credits.remaining')}: {creditsRemaining.toLocaleString(getLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
            <p className="text-muted-foreground mt-1">{creditUsagePercent.toFixed(1)}% {t('subscription.credits.utilized')}</p>
            {isExhausted && (
              <p className="text-red-500 font-semibold mt-2 border-t pt-2">
                ⚠️ {t('subscription.credits.resourcesLimited')}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
